import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useApp } from '@/context/AppContext';
import { 
  ShoppingCart, ChevronRight, ChevronLeft, Check, AlertCircle, X,
  Minus, Plus, Package, Truck, Shield, Award, RefreshCw, Phone,
  Star, Heart, Share2, ZoomIn, Box, Clock, MapPin, CreditCard, 
  Headphones, CheckCircle2, HelpCircle, FileText, Wrench, Info,
  Send, MessageSquare, User, Mail
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import ProductCard from '@/components/ProductCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default content if not set by admin
const defaultHighlights = [
  'Premium quality materials for long-lasting durability',
  'Easy installation with standard UAE plumbing fittings',
  'Modern design suitable for residential and commercial use',
  'Water-efficient design helps reduce utility bills',
  'Backed by manufacturer warranty and after-sales support',
  'Fast delivery across all UAE Emirates'
];

const defaultSpecifications = {
  'Brand': 'Grand Palace',
  'Material': 'Premium Grade',
  'Color': 'White / Chrome',
  'Installation Type': 'Floor / Wall Mounted',
  'Water Efficiency': 'Low Flow Technology',
  'Country of Origin': 'Imported'
};

const defaultBoxContents = [
  'Main Product Unit',
  'Mounting Hardware',
  'Installation Manual',
  'Warranty Card'
];

const defaultFaqs = [
  { q: 'Is installation included with the purchase?', a: 'Installation service is available at an additional cost. Contact our team for professional installation support across UAE.' },
  { q: 'What is the warranty period?', a: 'This product comes with a 1-year manufacturer warranty covering manufacturing defects.' },
  { q: 'Is this compatible with UAE plumbing standards?', a: 'Yes, this product is fully compatible with standard UAE plumbing connections.' },
  { q: 'What is the delivery time?', a: 'Standard delivery takes 2-5 business days within UAE.' },
  { q: 'Can I return the product?', a: 'Yes, we offer a 7-day return policy for unused products in original packaging.' }
];

// Enquiry Form Component
const EnquiryForm = ({ product, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    message: '',
    quantity: 1
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/enquiry`, {
        ...formData,
        product_id: product.id
      });
      toast.success('Enquiry submitted successfully! We will contact you soon.');
      onSuccess?.();
    } catch (err) {
      toast.error('Failed to submit enquiry. Please try again.');
    }
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Your Name *</Label>
        <Input required value={formData.customer_name} onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })} placeholder="Enter your full name" />
      </div>
      <div className="space-y-2">
        <Label>Email *</Label>
        <Input type="email" required value={formData.customer_email} onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })} placeholder="your@email.com" />
      </div>
      <div className="space-y-2">
        <Label>Phone Number *</Label>
        <Input required value={formData.customer_phone} onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })} placeholder="+971 50 123 4567" />
      </div>
      <div className="space-y-2">
        <Label>Quantity</Label>
        <Input type="number" min="1" value={formData.quantity} onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })} />
      </div>
      <div className="space-y-2">
        <Label>Message (Optional)</Label>
        <Textarea value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} placeholder="Any specific requirements or questions..." rows={3} />
      </div>
      <Button type="submit" disabled={loading} className="w-full bg-gold hover:bg-gold-dark text-navy-dark font-bold h-12">
        {loading ? 'Submitting...' : <><Send className="h-4 w-4 mr-2" /> Send Enquiry</>}
      </Button>
    </form>
  );
};

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useApp();
  const [product, setProduct] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [imageZoom, setImageZoom] = useState(false);
  const [showEnquiryDialog, setShowEnquiryDialog] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productRes, settingsRes] = await Promise.all([
          axios.get(`${API}/products/${id}`),
          axios.get(`${API}/settings`)
        ]);
        setProduct(productRes.data);
        setSettings(settingsRes.data);
        
        if (productRes.data.category) {
          const relatedRes = await axios.get(`${API}/products?category=${productRes.data.category}&limit=4`);
          setRelatedProducts(relatedRes.data.products?.filter(p => p.id !== id) || []);
        }
      } catch (e) {
        toast.error('Product not found');
      }
      setLoading(false);
    };
    fetchData();
    window.scrollTo(0, 0);
  }, [id]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          <div className="aspect-square skeleton rounded-xl" />
          <div className="space-y-4">
            <div className="h-8 w-48 skeleton rounded" />
            <div className="h-12 w-full skeleton rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <Package className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
        <h2 className="text-2xl font-bold">Product Not Found</h2>
        <Link to="/products">
          <Button className="mt-4 bg-gold text-navy-dark">Browse Products</Button>
        </Link>
      </div>
    );
  }

  const hasOffer = product.offer_price && product.offer_price < product.price;
  const displayPrice = hasOffer ? product.offer_price : product.price;
  const discount = hasOffer ? Math.round((1 - product.offer_price / product.price) * 100) : 0;
  const productImages = product.images?.length > 0 ? product.images : ["https://images.unsplash.com/photo-1586864387967-d02ef85d93e8?w=600"];
  
  // Check if both payment methods are disabled
  const paymentDisabled = !settings.payment_enabled && !settings.cod_enabled;
  
  // Use admin-defined content or defaults
  const highlights = product.highlights?.length > 0 ? product.highlights : defaultHighlights;
  const specifications = product.specifications && Object.keys(product.specifications).length > 0 
    ? { ...defaultSpecifications, ...product.specifications, 'Brand': product.brand || 'Grand Palace', 'Model / SKU': product.sku }
    : { ...defaultSpecifications, 'Brand': product.brand || 'Grand Palace', 'Model / SKU': product.sku };
  const boxContents = product.box_contents?.length > 0 ? product.box_contents : defaultBoxContents;
  const faqs = product.faqs?.length > 0 ? product.faqs : defaultFaqs;

  const handleAddToCart = () => {
    addToCart(product.id, quantity);
    toast.success('Added to cart successfully!');
  };

  const handleBuyNow = () => {
    addToCart(product.id, quantity);
    navigate('/cart');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b">
        <div className="container mx-auto px-4 py-3">
          <nav className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-gold">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="/products" className="hover:text-gold">Products</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-navy font-medium line-clamp-1">{product.name}</span>
          </nav>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Main Product Section */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          
          {/* Product Media */}
          <div className="space-y-4">
            <div 
              className="relative aspect-square rounded-xl overflow-hidden bg-white border cursor-zoom-in group"
              onClick={() => setImageZoom(true)}
            >
              <img 
                src={productImages[selectedImage]} 
                alt={product.name} 
                className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105" 
              />
              {hasOffer && (
                <Badge className="absolute top-4 left-4 bg-red-500 text-white text-lg px-4 py-2 font-bold">
                  {discount}% OFF
                </Badge>
              )}
              <div className="absolute top-4 right-4 flex flex-col gap-2">
                <button 
                  onClick={(e) => { e.stopPropagation(); setIsWishlisted(!isWishlisted); }}
                  className={`p-2 rounded-full bg-white shadow-md transition-colors ${isWishlisted ? 'text-red-500' : 'text-gray-400 hover:text-red-500'}`}
                >
                  <Heart className={`h-5 w-5 ${isWishlisted ? 'fill-current' : ''}`} />
                </button>
                <button 
                  onClick={(e) => { e.stopPropagation(); }}
                  className="p-2 rounded-full bg-white shadow-md text-gray-400 hover:text-navy"
                >
                  <Share2 className="h-5 w-5" />
                </button>
              </div>
              <div className="absolute bottom-4 right-4 p-2 rounded-full bg-white/80 text-gray-500">
                <ZoomIn className="h-5 w-5" />
              </div>
            </div>

            {productImages.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-2">
                {productImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImage === idx ? 'border-gold ring-2 ring-gold/30' : 'border-gray-200 hover:border-gold'
                    }`}
                  >
                    <img src={img} alt={`View ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 text-sm">
              <span className="text-navy font-medium">{product.brand || 'Grand Palace'}</span>
              <span className="text-muted-foreground">|</span>
              <span className="text-muted-foreground">SKU: {product.sku}</span>
            </div>

            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-navy leading-tight">
                {product.name}
              </h1>
              {product.name_ar && (
                <p className="text-lg text-gold font-arabic mt-2" dir="rtl">{product.name_ar}</p>
              )}
            </div>

            <div className="flex items-center gap-2">
              <div className="flex items-center">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} className={`h-5 w-5 ${i <= 4 ? 'text-gold fill-gold' : 'text-gray-300'}`} />
                ))}
              </div>
              <span className="text-sm text-muted-foreground">(4.0) 12 Reviews</span>
            </div>

            {/* Price Section */}
            <div className="bg-gray-50 rounded-xl p-6 space-y-4">
              <div className="flex items-end gap-4">
                <span className="text-4xl font-bold text-navy">AED {displayPrice?.toFixed(2)}</span>
                {hasOffer && (
                  <>
                    <span className="text-xl text-muted-foreground line-through">AED {product.price?.toFixed(2)}</span>
                    <Badge className="bg-green-500 text-white">Save AED {(product.price - product.offer_price).toFixed(2)}</Badge>
                  </>
                )}
              </div>
              <p className="text-sm text-muted-foreground">Inclusive of VAT</p>

              <div className="flex items-center gap-2">
                {product.stock > 10 ? (
                  <span className="flex items-center gap-2 text-green-600 font-medium">
                    <CheckCircle2 className="h-5 w-5" /> In Stock - Ready to Ship
                  </span>
                ) : product.stock > 0 ? (
                  <span className="flex items-center gap-2 text-orange-500 font-medium">
                    <AlertCircle className="h-5 w-5" /> Only {product.stock} left!
                  </span>
                ) : (
                  <span className="flex items-center gap-2 text-red-500 font-medium">
                    <X className="h-5 w-5" /> Out of Stock
                  </span>
                )}
              </div>

              {product.stock > 0 && (
                <div className="space-y-4 pt-4">
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-medium">Quantity:</span>
                    <div className="flex items-center border-2 border-gray-200 rounded-lg">
                      <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="p-3 hover:bg-gray-100">
                        <Minus className="h-4 w-4" />
                      </button>
                      <span className="w-16 text-center font-bold text-lg">{quantity}</span>
                      <button onClick={() => setQuantity(Math.min(product.stock, quantity + 1))} className="p-3 hover:bg-gray-100">
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Show Add to Cart buttons always - enquiry happens at checkout */}
                  <div className="flex gap-3">
                    <Button 
                      size="lg" 
                      className="flex-1 bg-gold hover:bg-gold-dark text-navy-dark font-bold h-14 text-lg"
                      onClick={handleAddToCart}
                      data-testid="add-to-cart-btn"
                    >
                      <ShoppingCart className="h-5 w-5 mr-2" /> Add to Cart
                    </Button>
                    <Button 
                      size="lg" 
                      variant="outline"
                      className="flex-1 border-2 border-navy text-navy hover:bg-navy hover:text-white font-bold h-14 text-lg"
                      onClick={handleBuyNow}
                    >
                      Buy Now
                    </Button>
                  </div>

                  <p className="text-center text-sm text-green-600 font-medium">
                    {paymentDisabled ? 'Add to cart and submit enquiry at checkout' : 'Order within 2 hours for same-day dispatch'}
                  </p>
                </div>
              )}
            </div>

            {/* Trust Elements - Infographic Style */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl border border-blue-200">
                <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center">
                  <Truck className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="font-bold text-navy">Free Delivery</p>
                  <p className="text-sm text-muted-foreground">Orders above AED 500</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-green-50 to-green-100 rounded-xl border border-green-200">
                <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                  <Shield className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="font-bold text-navy">Secure Payment</p>
                  <p className="text-sm text-muted-foreground">100% Protected</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-gold/10 to-gold/20 rounded-xl border border-gold/30">
                <div className="w-12 h-12 rounded-full bg-gold flex items-center justify-center">
                  <Award className="h-6 w-6 text-navy-dark" />
                </div>
                <div>
                  <p className="font-bold text-navy">Authentic Product</p>
                  <p className="text-sm text-muted-foreground">Brand Warranty</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl border border-purple-200">
                <div className="w-12 h-12 rounded-full bg-purple-500 flex items-center justify-center">
                  <Headphones className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="font-bold text-navy">After-Sales Support</p>
                  <p className="text-sm text-muted-foreground">Expert Assistance</p>
                </div>
              </div>
            </div>

            {/* Product Highlights - Infographic Style */}
            <div className="bg-gradient-to-br from-navy/5 to-navy/10 rounded-xl p-6 border">
              <h3 className="font-bold text-navy mb-4 flex items-center gap-2 text-lg">
                <CheckCircle2 className="h-6 w-6 text-gold" /> Product Highlights
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {highlights.map((highlight, idx) => (
                  <div key={idx} className="flex items-start gap-3 bg-white p-3 rounded-lg shadow-sm">
                    <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="h-3 w-3 text-white" />
                    </div>
                    <span className="text-sm">{highlight}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Product Tabs */}
        <div className="mt-12">
          <Tabs defaultValue="description" className="w-full">
            <TabsList className="w-full justify-start border-b rounded-none h-auto p-0 bg-transparent">
              <TabsTrigger value="description" className="rounded-none border-b-2 border-transparent data-[state=active]:border-gold data-[state=active]:text-gold px-6 py-4">
                Description
              </TabsTrigger>
              <TabsTrigger value="specifications" className="rounded-none border-b-2 border-transparent data-[state=active]:border-gold data-[state=active]:text-gold px-6 py-4">
                Specifications
              </TabsTrigger>
              <TabsTrigger value="delivery" className="rounded-none border-b-2 border-transparent data-[state=active]:border-gold data-[state=active]:text-gold px-6 py-4">
                Delivery
              </TabsTrigger>
            </TabsList>

            <TabsContent value="description" className="mt-6">
              <div className="max-w-3xl space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-navy mb-4">Product Description</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {product.description || `Experience premium quality with the ${product.name}. Designed for the modern UAE home and commercial spaces, this product combines durability with elegant aesthetics.`}
                  </p>
                </div>

                <div className="bg-gradient-to-r from-navy/5 to-transparent rounded-xl p-6">
                  <h3 className="text-xl font-bold text-navy mb-4 flex items-center gap-2">
                    <Box className="h-5 w-5 text-gold" /> What's Included
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {boxContents.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2 bg-white p-3 rounded-lg shadow-sm">
                        <Check className="h-4 w-4 text-green-500" />
                        <span className="text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="specifications" className="mt-6">
              <div className="max-w-2xl">
                <h3 className="text-xl font-bold text-navy mb-6">Technical Specifications</h3>
                <div className="grid grid-cols-1 gap-2">
                  {Object.entries(specifications).map(([key, value], idx) => (
                    <div key={key} className={`flex items-center justify-between p-4 rounded-lg ${idx % 2 === 0 ? 'bg-gray-50' : 'bg-white border'}`}>
                      <span className="font-medium text-navy">{key}</span>
                      <span className="text-muted-foreground">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="delivery" className="mt-6">
              <div className="max-w-2xl space-y-6">
                <h3 className="text-xl font-bold text-navy mb-4 flex items-center gap-2">
                  <Truck className="h-5 w-5 text-gold" /> Delivery Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-5 text-center">
                    <MapPin className="h-8 w-8 text-blue-500 mx-auto mb-2" />
                    <p className="font-bold text-navy">All UAE</p>
                    <p className="text-sm text-muted-foreground">Emirates covered</p>
                  </div>
                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-5 text-center">
                    <Clock className="h-8 w-8 text-green-500 mx-auto mb-2" />
                    <p className="font-bold text-navy">2-5 Days</p>
                    <p className="text-sm text-muted-foreground">Standard delivery</p>
                  </div>
                  <div className="bg-gradient-to-br from-gold/20 to-gold/30 rounded-xl p-5 text-center">
                    <RefreshCw className="h-8 w-8 text-gold mx-auto mb-2" />
                    <p className="font-bold text-navy">7-Day Returns</p>
                    <p className="text-sm text-muted-foreground">Easy returns</p>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* FAQ Section */}
        <div className="mt-12">
          <h3 className="text-xl font-bold text-navy mb-6 flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-gold" /> Frequently Asked Questions
          </h3>
          <div className="max-w-3xl space-y-4">
            {faqs.map((faq, idx) => (
              <details key={idx} className="border rounded-xl group bg-white shadow-sm">
                <summary className="flex items-center justify-between p-5 cursor-pointer text-navy font-medium hover:text-gold">
                  {faq.q}
                  <ChevronRight className="h-5 w-5 transform group-open:rotate-90 transition-transform" />
                </summary>
                <div className="px-5 pb-5 text-muted-foreground border-t pt-4">
                  {faq.a}
                </div>
              </details>
            ))}
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-12">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-navy">Related Products</h3>
              <Link to={`/products?category=${product.category}`} className="text-gold hover:text-gold-dark font-medium flex items-center gap-1">
                View All <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {relatedProducts.slice(0, 4).map(p => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Image Zoom Modal */}
      {imageZoom && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setImageZoom(false)}
        >
          <button 
            className="absolute top-4 right-4 text-white hover:text-gold"
            onClick={() => setImageZoom(false)}
          >
            <X className="h-8 w-8" />
          </button>
          <img 
            src={productImages[selectedImage]} 
            alt={product.name} 
            className="max-w-full max-h-full object-contain"
          />
        </div>
      )}
    </div>
  );
};

export default ProductDetailPage;
